import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Trash } from 'shared/icons';
import PageHeader from '../components/layout/PageHeader';
import Button from '../components/ui/Button';
import ConfirmModal from '../components/ui/ConfirmModal';
import Input from '../components/ui/Input';
import Modal from '../components/ui/Modal';
import Select from '../components/ui/Select';
import { SkeletonTable } from '../components/ui/Skeleton';
import StandardTable from '../components/ui/StandardTable';
import { useToast } from '../components/ui/Toast';
import { useAuth } from '../hooks/useAuth';
import api from '../services/api';

const defaultFormData = {
    name: '',
    type: 'SUPPLIER',
    assigned_brand: '',
    is_active: true,
};

const OrganizationsPage = () => {
    const toast = useToast();
    const { checkPermission } = useAuth();
    // Espelha o backend (requireAnyPermission(['organizations:write','users:write'])):
    // quem tem organizations:write (ex.: Karyne) gerencia orgs sem precisar de users:write.
    const canManage = checkPermission('organizations:write') || checkPermission('users:write');

    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingRow, setEditingRow] = useState(null);
    const [formData, setFormData] = useState(defaultFormData);
    const [saving, setSaving] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const fetchOrganizations = useCallback(async () => {
        try {
            setLoading(true);
            const data = await api.getOrganizations({ include_inactive: 'true' });
            const list = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
            setRows(list.filter((row) => String(row?.type || '').toUpperCase() !== 'ROOT'));
        } catch (error) {
            toast.error(error.message || 'Erro ao carregar organizacoes');
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [toast]);

    useEffect(() => {
        fetchOrganizations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openCreateModal = () => {
        setEditingRow(null);
        setFormData(defaultFormData);
        setModalOpen(true);
    };

    const openEditModal = (row) => {
        setEditingRow(row);
        setFormData({
            name: row?.name || '',
            type: row?.type || 'SUPPLIER',
            assigned_brand: row?.assigned_brand || '',
            is_active: Boolean(row?.is_active),
        });
        setModalOpen(true);
    };

    const saveOrganization = async () => {
        const payload = {
            name: String(formData.name || '').trim(),
            type: formData.type,
            assigned_brand: String(formData.assigned_brand || '').trim() || undefined,
            is_active: Boolean(formData.is_active),
        };

        if (payload.name.length < 2) {
            toast.error('Nome da organizacao e obrigatorio');
            return;
        }

        try {
            setSaving(true);
            if (editingRow?.id) {
                await api.updateOrganization(editingRow.id, payload);
                toast.success('Organizacao atualizada');
            } else {
                await api.createOrganization(payload);
                toast.success('Organizacao criada');
            }
            setModalOpen(false);
            await fetchOrganizations();
        } catch (error) {
            toast.error(error.message || 'Erro ao salvar organizacao');
        } finally {
            setSaving(false);
        }
    };

    const confirmDelete = async () => {
        if (!deleteTarget?.id) {
            return;
        }

        try {
            setDeleting(true);
            await api.deleteOrganization(deleteTarget.id);
            toast.success('Organizacao desativada');
            setDeleteTarget(null);
            await fetchOrganizations();
        } catch (error) {
            toast.error(error.message || 'Erro ao desativar organizacao');
        } finally {
            setDeleting(false);
        }
    };

    const columns = useMemo(
        () => [
            {
                key: 'name',
                label: 'Nome',
                getValue: (row) => row?.name || '-',
                render: (row) => <span className="font-medium text-black dark:text-white">{row?.name || '-'}</span>,
            },
            {
                key: 'type',
                label: 'Tipo',
                getValue: (row) => row?.type || '-',
            },
            {
                key: 'assigned_brand',
                label: 'Marca',
                getValue: (row) => row?.assigned_brand || '-',
            },
            {
                key: 'metrics',
                label: 'Metricas',
                sortable: false,
                searchable: false,
                getValue: (row) =>
                    `${Number(row?.metrics?.users || 0)} users | ${Number(row?.metrics?.products || 0)} products | ${Number(row?.metrics?.sales || 0)} sales`,
                render: (row) => (
                    <span className="text-xs text-mono-600 dark:text-mono-300">
                        {Number(row?.metrics?.users || 0)} users / {Number(row?.metrics?.products || 0)} products /{' '}
                        {Number(row?.metrics?.sales || 0)} sales
                    </span>
                ),
            },
            {
                key: 'is_active',
                label: 'Status',
                getValue: (row) => (row?.is_active ? 1 : 0),
                render: (row) => (
                    <span
                        className={`rounded-full px-2 py-1 text-xs font-semibold ${
                            row?.is_active
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                                : 'bg-mono-100 text-mono-700 dark:bg-mono-900 dark:text-mono-300'
                        }`}
                    >
                        {row?.is_active ? 'Ativa' : 'Inativa'}
                    </span>
                ),
            },
            {
                key: 'actions',
                label: 'Acoes',
                sortable: false,
                searchable: false,
                getValue: () => '',
                render: (row) =>
                    canManage ? (
                        <div className="flex flex-wrap gap-2">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    openEditModal(row);
                                }}
                            >
                                Editar
                            </Button>
                            {row?.is_active ? (
                                <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        setDeleteTarget(row);
                                    }}
                                >
                                    <Trash size={14} className="mr-1" />
                                    Desativar
                                </Button>
                            ) : (
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={async (event) => {
                                        event.stopPropagation();
                                        try {
                                            await api.updateOrganization(row.id, { is_active: true });
                                            toast.success('Organizacao reativada');
                                            await fetchOrganizations();
                                        } catch (error) {
                                            toast.error(error.message || 'Erro ao reativar organizacao');
                                        }
                                    }}
                                >
                                    Reativar
                                </Button>
                            )}
                        </div>
                    ) : (
                        <span className="text-xs text-mono-500">Somente leitura</span>
                    ),
            },
        ],
        [canManage, fetchOrganizations, toast],
    );

    if (loading) {
        return (
            <div className="space-y-6">
                <PageHeader title="Organizacoes" subtitle="Gestao de organizacoes do sistema" />
                <SkeletonTable columns={6} rows={10} />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <PageHeader
                title="Organizacoes"
                subtitle="Gestao de organizacoes do sistema"
                actions={
                    canManage ? (
                        <Button onClick={openCreateModal}>
                            <Plus size={16} className="mr-2" />
                            Nova organizacao
                        </Button>
                    ) : null
                }
            />

            <StandardTable
                rows={rows}
                columns={columns}
                rowKey={(row) => row.id}
                searchPlaceholder="Buscar organizacao..."
                emptyMessage="Nenhuma organizacao encontrada."
                exportEnabled
                exportFileName="organizacoes"
            />

            <Modal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                title={editingRow ? 'Editar organizacao' : 'Nova organizacao'}
                actions={
                    <>
                        <Button variant="secondary" onClick={() => setModalOpen(false)} disabled={saving}>
                            Cancelar
                        </Button>
                        <Button onClick={saveOrganization} loading={saving}>
                            Salvar
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <Input
                        label="Nome"
                        value={formData.name}
                        onChange={(event) => setFormData((current) => ({ ...current, name: event.target.value }))}
                    />
                    <Select
                        label="Tipo"
                        value={formData.type}
                        onChange={(event) => setFormData((current) => ({ ...current, type: event.target.value }))}
                    >
                        <option value="SUPPLIER">SUPPLIER</option>
                        <option value="HAULIER">HAULIER</option>
                        <option value="ROOT">ROOT</option>
                    </Select>
                    <Input
                        label="Marca vinculada (opcional)"
                        value={formData.assigned_brand}
                        onChange={(event) =>
                            setFormData((current) => ({ ...current, assigned_brand: event.target.value }))
                        }
                    />
                    <label className="flex items-center gap-2 text-sm text-mono-700 dark:text-mono-300">
                        <input
                            type="checkbox"
                            checked={formData.is_active}
                            onChange={(event) =>
                                setFormData((current) => ({ ...current, is_active: event.target.checked }))
                            }
                        />
                        Organizacao ativa
                    </label>
                </div>
            </Modal>

            <ConfirmModal
                isOpen={Boolean(deleteTarget)}
                onClose={() => setDeleteTarget(null)}
                onConfirm={confirmDelete}
                title="Desativar organizacao"
                message={
                    deleteTarget ? `Confirma desativar a organizacao "${deleteTarget.name}"?` : 'Confirma desativar?'
                }
                confirmText="Desativar"
                confirmVariant="danger"
                loading={deleting}
            />
        </div>
    );
};

export default OrganizationsPage;
